import { calculateHeaderGroupHeight } from "@theme/critical";
import { Component } from "@theme/component";
import { onDocumentLoaded, changeMetaThemeColor } from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty("--header-height", `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    // Check if this is a bottom-positioned header
    const isBottomPosition = this.classList.contains('header__sticky-position--bottom');
    const stickyPositionTop = this.hasAttribute('sticky-position-top');

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      // Update sticky-position-top attribute value depending on scroll position from top of the screen
      if (stickyPositionTop) {
        const rect = this.getBoundingClientRect();
        this.setAttribute('sticky-position-top', rect.top <= 0 ? 'true' : 'false');
      }

      if (alwaysSticky) {
        if (isBottomPosition) {
          // For bottom-positioned headers, active when at top, inactive otherwise
          const rect = this.getBoundingClientRect();
          this.dataset.stickyState = rect.top <= 0 ? "active" : "inactive";
        } else {
          this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        }
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove("desktop:hidden");
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add("hidden");
    } else {
      this.refs.headerDrawerContainer.classList.add("desktop:hidden");
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove("hidden");
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute("sticky");
    if (!this.#offscreen && stickyMode !== "always") return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isBottomPosition = this.classList.contains('header__sticky-position--bottom');
    
    // Update body data attribute based on scroll position relative to screen height
    const viewportHeight = window.innerHeight;
    if (scrollTop > viewportHeight) {
      document.body.dataset.scrolledPastScreen = "true";
    } else {
      document.body.dataset.scrolledPastScreen = "false";
    }
    
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === "always") {
      if (isBottomPosition) {
        // For bottom-positioned headers
        const rect = this.getBoundingClientRect();
        
        if (rect.top <= 0) {
          // Header has reached the top and should stick
          this.dataset.stickyState = "active";
          this.dataset.scrollDirection = "none";
        } else {
          // Header is still scrolling up with the page
          this.dataset.stickyState = "inactive";
          if (isScrollingUp) {
            this.dataset.scrollDirection = "up";
          } else {
            this.dataset.scrollDirection = "down";
          }
        }
      } else {
        // Original logic for top-positioned headers
        const isAtTop = this.getBoundingClientRect().top >= 0;

        if (isAtTop) {
          this.dataset.scrollDirection = "none";
        } else if (isScrollingUp) {
          this.dataset.scrollDirection = "up";
        } else {
          this.dataset.scrollDirection = "down";
        }
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute("data-animating");

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = "none";
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      }
    } else if (this.dataset.stickyState === "active") {
      this.dataset.scrollDirection = "none";
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute("data-animating", "");

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = "idle";
        this.removeAttribute("data-animating");
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = "none";
      this.dataset.stickyState = "idle";
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
      }
    }
    
    // Set initial scroll state on page load
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const viewportHeight = window.innerHeight;
    document.body.dataset.scrolledPastScreen = scrollTop > viewportHeight ? "true" : "false";
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
    document.removeEventListener("scroll", this.#handleWindowScroll);
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

/**
 * Moves the header group below the first section wrapper for bottom sticky position
 */
function moveHeaderToBottom() {
  const headerGroup = document.getElementById('header-group');
  const dynamicSections = document.querySelector('.dynamic-sections');
  const firstSectionWrapper = dynamicSections?.querySelector('.section-wrapper');
  
  if (headerGroup && firstSectionWrapper && dynamicSections) {
    firstSectionWrapper.after(headerGroup);
    headerGroup.classList.add('positioned');
  }
}

onDocumentLoaded(() => {
  /** @type {HTMLElement | null} */
  const header = /** @type {HTMLElement | null} */ (
    document.querySelector("#header-component")
  );
  /** @type {HTMLElement | null} */
  const headerGroup = /** @type {HTMLElement | null} */ (
    document.querySelector("#header-group")
  );

  // Move header if needed for bottom sticky position
  if (header?.dataset.moveToBottom === 'true') {
    moveHeaderToBottom();
  }

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() =>
      calculateHeaderGroupHeight(header, headerGroup),
    );

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement))
              continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }

  const hero = document.querySelector("[data-hero-component]");
  if (header && hero) {
    // Helper to set color scheme safely on the header component
    /** @param {string} scheme */
    const setColorScheme = (scheme) => {
      const target = header; // apply to header-component
      const cls = target.className;
      if (/color-scheme-\d+/.test(cls)) {
        target.className = cls.replace(
          /color-scheme-\d+/g,
          `color-scheme-${scheme}`,
        );
      } else {
        target.classList.add(`color-scheme-${scheme}`);
      }
    };

    // Store original color scheme and transparent attribute once on the header component
    if (!header.dataset.originalColorScheme) {
      const match = header.className.match(/color-scheme-(\d+)/);
      header.dataset.originalColorScheme = match ? match[1] : "1";
    }
    
    // Store the original transparent attribute value
    if (!header.dataset.originalTransparent && header.hasAttribute('transparent')) {
      header.dataset.originalTransparent = header.getAttribute('transparent');
    }

    // Track current state to prevent unnecessary updates
    let isTransparent = header.hasAttribute('transparent');
    let isUpdating = false;

    // Create an observer that flips when the hero's bottom passes the header's bottom
    /** @type {IntersectionObserver | null} */
    let heroObserver = null;
    const getHeaderHeight = () =>
      Math.round(header.getBoundingClientRect().height);
    const createHeroObserver = () => {
      if (heroObserver) heroObserver.disconnect();

      const headerHeight = getHeaderHeight();
      heroObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry || isUpdating) return;
          const { isIntersecting } = entry;

          // Only update if the state actually needs to change
          const shouldBeTransparent = isIntersecting;
          if (shouldBeTransparent === isTransparent) return;

          isUpdating = true;
          isTransparent = shouldBeTransparent;

          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            if (isIntersecting) {
              // Hero is still beneath the header boundary — restore original scheme
              const originalScheme = header.dataset.originalColorScheme || "1";
              setColorScheme(originalScheme);
              
              // Remove the opaque class to show transparent header
              header.classList.remove('header--opaque');
            } else {
              // Scrolled past hero — use color scheme 1 and add opaque class
              setColorScheme("1");
              header.classList.add('header--opaque');
            }

            // Allow next update after a short delay to prevent rapid toggling
            setTimeout(() => {
              isUpdating = false;
            }, 100);
          });
        },
        {
          root: null,
          threshold: 0,
          // Flip exactly when hero bottom crosses header bottom
          rootMargin: `-${headerHeight}px 0px 0px 0px`,
        },
      );

      heroObserver.observe(hero);
    };

    createHeroObserver();

    // Recreate observer when header size changes or on window resize
    const headerResizeObserver = new ResizeObserver(() => createHeroObserver());
    headerResizeObserver.observe(header);
    window.addEventListener("resize", createHeroObserver, { passive: true });
  }
});
